import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Nav } from "@/components/Nav";
import { Footer } from "@/components/Footer";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/Logo";
import { api } from "@/lib/api";
import { Loader2, Mail, ArrowLeft, KeyRound } from "lucide-react";

export default function ForgotPassword() {
    const nav = useNavigate();
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const [sent, setSent] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!email.trim()) return;
        setLoading(true);
        try {
            await api.post("/auth/forgot-password", { email: email.trim() });
            setSent(true);
            toast.success("Check your email for the 6-digit code.");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't send reset code. Try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen pp-bg-0 text-white">
            <Nav />
            <section className="py-16 sm:py-24">
                <div className="max-w-md mx-auto px-4">
                    <Button asChild variant="ghost" className="mb-4 text-[#8b8ba0] hover:text-white">
                        <Link to="/login" className="inline-flex items-center gap-1" data-testid="forgot-back-to-login">
                            <ArrowLeft className="w-4 h-4" /> Back to login
                        </Link>
                    </Button>
                    <div className="flex items-center gap-3 mb-6">
                        <Logo size={36} />
                        <div>
                            <p className="text-xs font-mono uppercase text-[#22d3ee] tracking-widest">Password reset</p>
                            <h1 className="font-display text-3xl tracking-widest">FORGOT PASSWORD</h1>
                        </div>
                    </div>
                    <Card className="pp-bg-1 border-[#242436] text-white">
                        <CardContent className="p-6">
                            {!sent ? (
                                <>
                                    <p className="text-sm text-[#b7b7c6] mb-4">
                                        Enter the email tied to your HOTSLIP account. We'll send you a 6-digit code.
                                    </p>
                                    <form onSubmit={submit} className="space-y-4" data-testid="forgot-password-form">
                                        <div>
                                            <Label htmlFor="email" className="text-[#b7b7c6]">Email</Label>
                                            <Input
                                                id="email"
                                                type="email"
                                                required
                                                autoFocus
                                                value={email}
                                                onChange={(e) => setEmail(e.target.value)}
                                                placeholder="you@email.com"
                                                className="mt-1 pp-bg-0 border-[#242436] text-white placeholder:text-[#5e5e72]"
                                                data-testid="forgot-email-input"
                                            />
                                        </div>
                                        <Button type="submit" disabled={loading} className="w-full h-11 bg-[#22d3ee] hover:brightness-110 text-[#062229] font-semibold" data-testid="forgot-submit-button">
                                            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <span className="inline-flex items-center gap-2"><Mail className="w-4 h-4" /> Send me a code</span>}
                                        </Button>
                                    </form>
                                </>
                            ) : (
                                <div className="text-center py-6" data-testid="forgot-sent-state">
                                    <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#07130b] border border-[#22c55e]66 mb-4">
                                        <Mail className="w-6 h-6 text-[#22c55e]" />
                                    </div>
                                    <p className="font-display text-xl tracking-widest mb-2">CHECK YOUR EMAIL</p>
                                    <p className="text-sm text-[#b7b7c6] mb-6">
                                        If <span className="text-white font-semibold">{email}</span> is registered, we just sent you a 6-digit code. It expires in 15 minutes.
                                    </p>
                                    <Button asChild className="w-full h-11 bg-[#6f3fec] hover:brightness-110 text-white" data-testid="forgot-enter-code-button">
                                        <Link to={`/reset-password?email=${encodeURIComponent(email)}`} className="inline-flex items-center gap-2">
                                            <KeyRound className="w-4 h-4" /> Enter the code
                                        </Link>
                                    </Button>
                                    <button onClick={() => { setSent(false); setEmail(""); }} className="mt-3 text-xs text-[#8b8ba0] hover:text-white" data-testid="forgot-retry-button">
                                        Didn't get it? Try a different email
                                    </button>
                                </div>
                            )}
                            <p className="mt-4 text-sm text-[#b7b7c6] text-center">
                                Remember it?{" "}
                                <Link to="/login" className="text-[#22c55e] hover:underline">Log in</Link>
                            </p>
                        </CardContent>
                    </Card>
                </div>
            </section>
            <Footer />
        </div>
    );
}
